import React, {Component} from 'react'
import PropTypes from 'prop-types'
import {PRICE_ERRORS} from 'constants/errors'
import {fetchMJOData} from 'apis/mjo'
import {noop, bindAll, getComponentDisplayName} from 'utils'
import Radium from './configured-radium'
import ConnectLocation from './connect-location'

const PriceUnavailable = Radium(({currentState = '', locationError = '', onClick = noop}) => {
  if (!currentState) {
    return (
      <button style={priceStyles.button} onClick={onClick}>
        Enter your <span style={priceStyles.highlight}>postcode</span> to see accurate pricing in
        your area.
      </button>
    )
  }
  
  return (
    <button style={priceStyles.button} onClick={onClick}>
      {PRICE_ERRORS.LAST_RESORT}
    </button>
  )
})

const priceStyles = {
  button: {
    letterSpacing: '0.1em',
    outline: 'none',
    border: 'none',
    backgroundColor: 'transparent',
    margin: 0,
    padding: 0,
    fontFamily: 'inherit',
    fontSize: 'inherit',
    lineHeight: 1.4,
    color: 'inherit',
    cursor: 'pointer',
    textTransform: 'inherit',
    ':hover': {
      outline: 'none',
    },
    ':active': {
      outline: 'none',
    },
    ':focus': {
      outline: 'none',
    },
  },
  highlight: {
    color: '#ab0000',
    borderBottom: '1px solid #999',
  },
}

/**
 * Higher order component for Fetching and determining price based on geographical location.
 * @param {Object} options - Initial config options.
 * @param {String} options.expose - Name of the prop object that contains price data & methods.
 * @returns {Function} The configured higher order component, which in turn will return a component class.
 */
export default ({expose = 'price'} = {}) => (Wrapped) => {
  class Price extends Component {
    constructor(props) {
      super(props)
      this.state = {
        currentState: this.props.locationState.state,
        data: [false],
        fetchURL: false,
        fetch: false,
        mapping: {},
      }
      
      bindAll(
        [
          'setOptions',
          'fetchPrice',
          'renderPriceUnavailable',
          'updatePriceLocation',
          'updatePriceLocationToState',
          'updatePriceStaticData',
          'testMapping',
          'updatePrice',
          'priceMode',
        ],
        this,
      )
    }
    
    /**
     * Set options for specs api on componentDidMount() / componentWillMount()
     * @param {Object} options - Options object.
     * @param {String} options.fetchURL - Url for spec api endpoint.
     * @param {Function} options.mapping - A mapping function between api values and state.
     * @param {Function} [callback] - Callback when new options have been updated in state.
     */
    setOptions(options, callback) {
      const {fetchURL, mapping} = options
      this.setState(
        {
          fetchURL,
          mapping,
          fetch: true,
        },
        () => {
          if (typeof callback === 'function') callback()
        },
      )
    }
    
    fetchPrice(stateOptions) {
      return fetchMJOData(this.state.fetchURL, this.state.mapping(stateOptions))
    }
    
    /**
     * Applys business logic to array of state prices
     * @param {Array} priceArray - Array of objects containing geographic state prices.
     * @returns {Array} - Objects containing price or error messages
     */
    updatePriceLocation(priceArray) {
      const {currentState} = this.state
      if (!Array.isArray(priceArray)) priceArray = [priceArray]
      return priceArray.map((priceStateArray) => {
        if (!priceStateArray) return false
        const locationPrice = priceStateArray.find(item => item.state.toUpperCase() === currentState.toUpperCase())
        
        return locationPrice && locationPrice.price
          ? locationPrice
          : {message: this.renderPriceUnavailable()}
      })
    }
    
    /**
     * Update price when connect-location HOC receives the current state
     */
    componentWillReceiveProps({locationState}) {
      const {currentState} = this.state
      
      if (locationState.state && locationState.state !== currentState) {
        this.setState({currentState: locationState.state}, () =>
          this.updatePriceLocationToState(this.rawPriceArray))
      }
    }
    
    updatePriceStaticData(priceArray) {
      this.rawPriceArray = priceArray
      this.updatePriceLocationToState(this.rawPriceArray)
    }
    
    /**
     * Takes array of prices by state and saves the current states price to state
     * @param {Array} priceArray - Array of objects containing geographic state prices.
     */
    updatePriceLocationToState(priceArray) {
      const {data} = this.state
      const newData = this.updatePriceLocation(priceArray).map((price, index) => price || data[index])
      this.setState({data: newData})
    }
    
    testMapping(stateOptions) {
      let test
      try {
        test = this.state.mapping(stateOptions)
      } catch (error) {
        return false
      }
      return Object.keys(test).every(key => typeof test[key] !== 'undefined')
    }
    
    /**
     * Take state object & maps it to API values then makes fetch request & updates state.
     * @param {Object} stateOptions - Options stored in state
     */
    updatePrice(stateOptions) {
      if (!this.state.fetch) throw new Error('Set setOptions() before update()')
      if (!Array.isArray(stateOptions)) stateOptions = [stateOptions]
      
      const {data} = this.state
      const newData = stateOptions.map((opt, index) => (opt ? {message: PRICE_ERRORS.LOADING}: data[index]))
      
      this.setState({data: newData}, () => {
        Promise.all(stateOptions.map((optionSet) => {
            const resp = this.testMapping(optionSet)
            return resp && this.fetchPrice(optionSet)
          }))
          .then((priceArray) => {
            // Store this so that it can be re-parsed when postcode is received.
            this.rawPriceArray = priceArray || []
            this.updatePriceLocationToState(this.rawPriceArray)
          })
          .catch(error => console.warn(error))
      })
    }
    
    priceMode() {
      return this.state.fetch
        ? {update: this.updatePrice}
        : {update: this.updatePriceStaticData}
    }
    
    renderPriceUnavailable() {
      return (
        <PriceUnavailable
          currentState={this.state.currentState}
          locationError={this.props.locationState.fetchError}
          onClick={this.props.locationActions.requestPostcodeInput}/>
      )
    }
    
    render() {
      return (
        <Wrapped
          {...{
            ...this.props,
            [expose]: {
              data: this.state.data,
              setOptions: this.setOptions,
              renderPriceUnavailable: this.renderPriceUnavailable,
              ...this.priceMode(),
            },
          }} />
      )
    }
  }
  
  Price.propTypes = {
    locationState: PropTypes.object,
    locationActions: PropTypes.object,
  }
  
  Price.displayName = `Price(${getComponentDisplayName(Wrapped)})`
  
  return ConnectLocation({
    exposeState: 'locationState',
    exposeActions: 'locationActions',
  })(Price)
}



// WEBPACK FOOTER //
// ./src/higher-order-components/price.jsx